/* ────────────────────────────────────────────────────────────────────────
   Curb Vault QR — shared helpers (browser-side image manipulation)

   The "Vault QR" trick: a normal QR code with one of its three finder
   patterns (the big squares in the corners) digitally erased before
   printing. Standard scanners fail because they can't lock on with
   only 2/3 finders. Our scanner page rebuilds the missing finder in
   a canvas pre-pass before handing the image to jsQR.

   Two helpers exported:
     - wipeFinderTopRight(canvas)       — used by /vault/new generator
     - reconstructFinderTopRight(imageData) — used by /vault/scan reader

   Implementation notes:
     - We pick the TOP-RIGHT finder as the "missing-piece key" because
       it's the most distinctive visually and easiest to detect by
       geometry from the other two (top-left + bottom-left form an
       L; the missing corner completes the rectangle).
     - The finder pattern in QR spec is 7×7 modules (squares):
         #######
         #     #
         # ### #
         # ### #
         # ### #
         #     #
         #######
       We rebuild that exact mask scaled to the QR's module size.
   ──────────────────────────────────────────────────────────────────────── */

// 7x7 finder pattern as defined by ISO/IEC 18004. 1=black, 0=white.
const FINDER_MASK = [
  [1, 1, 1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 1, 1, 1, 1, 1, 1],
];

/**
 * Detect the on-canvas geometry of the rendered QR: the offset of the
 * QR's top-left module and the module size (in CSS px). We assume a
 * square QR centred in the canvas with consistent margins. Returns
 * { qrX, qrY, moduleSize, qrSizePx } or null.
 *
 * Strategy: scan rows from the top-left until we find the first black
 * pixel, then walk right along the top edge to measure module size by
 * counting black pixels in the top-left finder's outer row.
 */
export function probeQrGeometry(ctx, w, h) {
  const data = ctx.getImageData(0, 0, w, h).data;
  const isBlack = (x, y) => {
    const i = (y * w + x) * 4;
    // average RGB < 128 = black-ish (works with antialiased renders)
    return (data[i] + data[i + 1] + data[i + 2]) / 3 < 128;
  };

  // Find first black pixel scanning diagonally from top-left.
  let qrX = -1;
  let qrY = -1;
  outer: for (let d = 0; d < Math.min(w, h); d++) {
    for (let i = 0; i <= d; i++) {
      const x = i;
      const y = d - i;
      if (isBlack(x, y)) {
        qrX = x;
        qrY = y;
        break outer;
      }
    }
  }
  if (qrX < 0) return null;

  // Walk right along the top edge of the finder pattern to measure
  // the run length of consecutive black pixels = 7 modules.
  let run = 0;
  let x = qrX;
  while (x < w && isBlack(x, qrY)) {
    run++;
    x++;
  }
  if (run < 14) return null; // QR too small / detection failed
  const moduleSize = run / 7;

  // Now find the *right* edge by scanning the same row from the right
  // and detecting the first black pixel (top of top-right finder).
  let qrRight = -1;
  for (let xr = w - 1; xr > qrX; xr--) {
    if (isBlack(xr, qrY)) {
      qrRight = xr;
      break;
    }
  }
  if (qrRight < 0) return null;
  const qrSizePx = qrRight - qrX + 1;

  return { qrX, qrY, moduleSize, qrSizePx };
}

/**
 * Erase the top-right finder pattern by painting a white square over
 * it. Used at generation time. canvas is the freshly-rendered QR.
 *
 * We wipe an area BIGGER than just the 7×7 finder + separator. jsQR's
 * fallback logic can sometimes decode a QR with only two finders, which
 * defeats the demo. Wiping ~9 modules square destroys both the finder
 * and several timing-pattern bits, guaranteeing the first-pass decode
 * fails and reconstruction is required.
 */
export function wipeFinderTopRight(canvas) {
  const ctx = canvas.getContext("2d");
  const geom = probeQrGeometry(ctx, canvas.width, canvas.height);
  if (!geom) return false;
  const { qrX, qrY, moduleSize, qrSizePx } = geom;

  // Top-right finder is 7 modules. Add 1 module of separator (whitespace
  // per QR spec) so the wipe also clears the separator, but no further —
  // wiping more destroys timing-pattern bits we can't reconstruct.
  const wipeModules = 8;
  const wipePx = wipeModules * moduleSize;
  const wipeX = qrX + qrSizePx - wipePx + moduleSize * 0.5;
  const wipeY = qrY - moduleSize * 0.5;

  ctx.save();
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(wipeX, wipeY, wipePx, wipePx);
  ctx.restore();
  return true;
}

/**
 * The inverse: given an ImageData object (camera frame), find the two
 * remaining finder patterns and synthesize the missing top-right one
 * back into the pixels so jsQR can decode normally.
 *
 * Returns a NEW ImageData with the finder painted in, or null if we
 * couldn't reliably detect the geometry.
 */
export function reconstructFinderTopRight(imageData) {
  const { width: w, height: h, data } = imageData;
  const isBlack = (x, y) => {
    const i = (y * w + x) * 4;
    return (data[i] + data[i + 1] + data[i + 2]) / 3 < 128;
  };

  // Find top-left finder: first solid black region from top-left, big enough
  // to be a finder (we look for at least ~12px black run on its top edge).
  let tlX = -1;
  let tlY = -1;
  outer: for (let y = 0; y < h - 14; y++) {
    for (let x = 0; x < w - 14; x++) {
      if (!isBlack(x, y)) continue;
      // measure horizontal run
      let run = 0;
      while (x + run < w && isBlack(x + run, y)) run++;
      if (run < 12) {
        x += run; // skip
        continue;
      }
      // measure vertical run from start of horizontal run
      let vRun = 0;
      while (y + vRun < h && isBlack(x, y + vRun)) vRun++;
      if (vRun < 12) {
        x += run;
        continue;
      }
      tlX = x;
      tlY = y;
      break outer;
    }
  }
  if (tlX < 0) return null;

  // Module size from top-left finder (7 modules wide).
  let run = 0;
  while (tlX + run < w && isBlack(tlX + run, tlY)) run++;
  const moduleSize = run / 7;
  if (moduleSize < 1.2) return null; // too tiny to reconstruct safely

  // Find bottom-left finder: scan down the *left edge* of the QR (column tlX)
  // for the next big black block after a whitespace gap.
  let blY = -1;
  let inWhite = false;
  for (let y = tlY + 7 * moduleSize; y < h - 7 * moduleSize; y++) {
    if (!inWhite && !isBlack(tlX, y)) inWhite = true;
    if (inWhite && isBlack(tlX, y)) {
      // measure horizontal run at this row
      let r = 0;
      while (tlX + r < w && isBlack(tlX + r, y)) r++;
      if (r >= 6 * moduleSize) {
        blY = y;
        break;
      }
    }
  }
  if (blY < 0) return null;

  // The QR is square. Right edge X = tlX + (blY - tlY) (since width = height of QR area).
  const qrSidePx = blY - tlY + 7 * moduleSize;
  const trX = tlX + qrSidePx - 7 * moduleSize;
  const trY = tlY;

  // Paint the 7×7 finder mask into the data buffer at (trX, trY).
  const out = new Uint8ClampedArray(data);
  for (let my = 0; my < 7; my++) {
    for (let mx = 0; mx < 7; mx++) {
      if (!FINDER_MASK[my][mx]) continue;
      // Fill the module square with black pixels.
      const sx = Math.round(trX + mx * moduleSize);
      const sy = Math.round(trY + my * moduleSize);
      const ex = Math.round(trX + (mx + 1) * moduleSize);
      const ey = Math.round(trY + (my + 1) * moduleSize);
      for (let py = sy; py < ey; py++) {
        for (let px = sx; px < ex; px++) {
          if (px < 0 || py < 0 || px >= w || py >= h) continue;
          const i = (py * w + px) * 4;
          out[i] = 0;
          out[i + 1] = 0;
          out[i + 2] = 0;
          out[i + 3] = 255;
        }
      }
    }
  }
  return new ImageData(out, w, h);
}
